import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import { useTheme } from '@mui/material/styles'
import Typography from '@/v2/components/common/Typography'
import Button from '@/v2/components/common/Button'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import useModals from '@/v2/hooks/useModals'
import { useClaims } from '@/v2/hooks/insurance/useClaims'
import RTClaims from '@/v2/hooks/subscriptions/messages/RTClaims'

const ClaimCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`

const ClaimTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`

const StatusBadge = styled.div`
  background: ${({ bgColor }) => bgColor || '#f5f5f5'};
  color: ${({ color }) => color || '#fff'};
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  gap: 16px;
`

const InfoLabel = styled.div`
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
`

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  text-align: right;
  word-break: break-word;
`

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #666;
  font-size: 14px;
  background: #fff;
  border-radius: 12px;
`

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #666;
  font-size: 14px;
`

const ClaimsTabMobile = () => {
  const theme = useTheme()
  const { goTo } = useModals()
  const { id } = useParams()
  const { patient } = usePatientContext()
  const { claims, isLoading, isError } = useClaims(id)
  const { onOpenSnackbar } = useSnackbarContext()

  useEffect(() => {
    if (!isLoading && !!isError) {
      onOpenSnackbar("It wasn't possible to load the patient claims.", 'error')
    }
  }, [isLoading, isError, onOpenSnackbar])

  if (isLoading) {
    return <LoadingState>Loading...</LoadingState>
  }

  const handleCreateClaim = () => {
    goTo(`v2/patient/${patient.id}/claim/view`, {
      modalType: 'create',
      patientId: patient.id,
      patient,
    })
  }

  if (!claims || claims.length === 0) {
    return (
      <>
        <ActionBar>
          <Button variant="text" onClick={handleCreateClaim}>
            Create Claim
          </Button>
        </ActionBar>
        <EmptyState>No claims found</EmptyState>
      </>
    )
  }

  const getStatusColor = status => {
    const colors = {
      pending: { bg: '#fff3e0', color: theme.palette.warning.main },
      invalidated: { bg: '#ffebee', color: theme.palette.error.main },
      answered: { bg: '#e3f2fd', color: '#1976d2' },
      validated: { bg: '#e8f5e9', color: theme.palette.success.main },
      submitted: { bg: '#e3f2fd', color: '#1976d2' },
      approved: { bg: '#e8f5e9', color: theme.palette.success.main },
      denied: { bg: '#ffebee', color: theme.palette.error.main },
      cancelled: { bg: '#f5f5f5', color: '#666' },
      failed: { bg: '#ffebee', color: theme.palette.error.main },
    }
    return colors[status] || { bg: '#f5f5f5', color: '#666' }
  }

  const getStatusLabel = status => {
    const labels = {
      pending: 'Pending',
      invalidated: 'Invalidated',
      answered: 'Answered',
      validated: 'Validated',
      submitted: 'Submitted',
      approved: 'Approved',
      denied: 'Denied',
      cancelled: 'Cancelled',
      failed: 'Failed',
    }
    return labels[status] || status?.toUpperCase() || 'UNKNOWN'
  }

  const handleStatusClick = claim => {
    const { status, insurance, id: claimId } = claim

    if (
      status === 'pending' ||
      status === 'invalidated' ||
      status === 'answered'
    ) {
      goTo(`/v2/patient/${patient.id}/claim/edit`, {
        modalType: 'edit',
        patientId: patient.id,
        isEdit: true,
        insuranceId: insurance?.id,
        claim,
        patient,
      })
    } else if (
      status === 'validated' ||
      status === 'submitted' ||
      status === 'approved' ||
      status === 'denied' ||
      status === 'cancelled' ||
      status === 'failed'
    ) {
      goTo(`/v2/patient/${patient.id}/claim/view`, {
        modalType: 'view',
        patientId: patient.id,
        claimId,
        insuranceId: insurance?.id,
        claim,
      })
    }
  }

  return (
    <>
      <RTClaims />
      <ActionBar>
        <Button variant="text" onClick={handleCreateClaim}>
          Create Claim
        </Button>
      </ActionBar>
      {claims.map(claim => {
        const statusColors = getStatusColor(claim.status)
        return (
          <ClaimCard key={claim.id}>
            <CardHeader>
              <div style={{ flex: 1 }}>
                <ClaimTitle>Claim #{claim.id}</ClaimTitle>
              </div>
              <StatusBadge
                bgColor={statusColors.bg}
                color={statusColors.color}
                onClick={() => handleStatusClick(claim)}
                style={{ cursor: 'pointer' }}
              >
                {getStatusLabel(claim.status)}
              </StatusBadge>
            </CardHeader>

            <InfoRow>
              <InfoLabel>Insurance</InfoLabel>
              <InfoValue>{claim.insurance?.payer?.name || 'N/A'}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Subscriber</InfoLabel>
              <InfoValue>
                {claim.subscriber?.firstName || claim.subscriber?.name || '-'}{' '}
                {claim.subscriber?.lastName || ''}
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Created</InfoLabel>
              <InfoValue>
                {safeFormat(parseISO(claim.createdAt), 'MMM dd, yyyy')}
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Last Edit</InfoLabel>
              <InfoValue>
                {safeFormat(parseISO(claim.updatedAt), 'MMM dd, yyyy')}
              </InfoValue>
            </InfoRow>

            <CardActions>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleStatusClick(claim)}
                fullWidth
              >
                View Details
              </Button>
            </CardActions>
          </ClaimCard>
        )
      })}
    </>
  )
}

export default ClaimsTabMobile
